import StaffNoteWithdrawalDialog from "./StaffNoteWithdrawalDialog";
import { createClientRequestId } from "../../utils/createClientRequestId";
import React, { useMemo, useState, useEffect } from "react";
import {
  Alert,
  Button,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebase";
import { useRole } from "../../contexts/RoleContext";
import { useChildContext } from "../../contexts/ChildContext";
import useMyStaffNotes from "./useMyStaffNotes";
import { dateKeyInZone, filterStaffNotes, summarizeStaffNotes } from "./staffNotePresentation";
import { mutateStaffNote, signStaffNotes } from "./staffNotesService";
import StaffNoteCard from "./StaffNoteCard";
import StaffNoteEditor from "./StaffNoteEditor";
import StaffSigningDialog from "./StaffSigningDialog";
export default function MyNotesTodayPage() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { childrenWithAccess = [], getUserRoleForChild } = useRole();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const child = childrenWithAccess.find((child) => child.id === currentChildId);
  const enabled = Boolean(
    user &&
    getUserRoleForChild?.(child?.id) === "caregiver" &&
    child?.staffCaregivers?.[user.uid]?.enabled,
  );
  const data = useMyStaffNotes(child?.id, enabled, user?.uid);
  const [chosenDay, setChosenDay] = useState(""),
    [view, setView] = useState("care"),
    [editing, setEditing] = useState(null),
    [signing, setSigning] = useState(null),
    [withdrawing, setWithdrawing] = useState(null);
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  const day =
    chosenDay ||
    (data.context ? dateKeyInZone(now, data.context.timeZone) : "");
  const visible = useMemo(
    () =>
      data.context
        ? filterStaffNotes({
            notes: data.notes,
            day,
            view,
            timeZone: data.context.timeZone,
          })
        : [],
    [data.notes, data.context, day, view],
  );
  const counts = summarizeStaffNotes(visible);
  const ready = visible.filter(
    (note) => !note.signature && note.readiness?.ready && !note.loadError,
  );
  const undated = data.notes.filter(
    (note) => !note.withdrawn && !note.version?.careEvents.length,
  ).length;
  const older = data.notes.filter(
    (note) =>
      !note.withdrawn &&
      !note.signature &&
      note.version?.careEvents.some(
        (event) => event.date < dateKeyInZone(now, data.context.timeZone),
      ),
  ).length;
  const today = data.context ? dateKeyInZone(now, data.context.timeZone) : "";
  const otherCareDates = data.notes.filter(
    (note) =>
      !note.withdrawn &&
      note.version &&
      dateKeyInZone(note.version.documentedAt, data.context.timeZone) ===
        today &&
      !note.version.careEvents.some((event) => event.date === today),
  ).length;
  const change = async (note, operation, changes = {}) => {
    await mutateStaffNote({
      childId: child.id,
      captureId: note.captureId,
      expectedVersionId: note.version.versionId,
      operation,
      ...changes,
    });
    await data.refresh();
    window.dispatchEvent(
      new CustomEvent("captureez:timeline-refresh", {
        detail: { childId: child.id },
      }),
    );
  };
  const action = async (callback) => {
    setBusy(true);
    setMessage("");
    try {
      await callback();
    } catch (error) {
      setMessage(error.message || "This action did not finish.");
    } finally {
      setBusy(false);
    }
  };
  const sign = async (notes, attestationVersion) => {
    const result = await signStaffNotes({
      childId: child.id,
      attestationVersion,
      notes: notes.map((note) => ({
        captureId: note.captureId,
        expectedVersionId: note.version.versionId,
      })),
    });
    const successes = result.results.filter((item) => item.ok),
      failures = result.results.filter((item) => !item.ok);
    setMessage(
      `${successes.length} signed in this action.${failures.length ? ` ${failures.length} not signed: ${failures.map((item) => item.message).join(" ")}` : ""}`,
    );
    await data.refresh();
    window.dispatchEvent(new CustomEvent("captureez:staff-notes-refresh", { detail: { childId: child.id } }));
  };
  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Button
          onClick={() => navigate("/home")}
          sx={{ alignSelf: "flex-start" }}
        >
          Back to Home
        </Button>
        <Typography variant="h4" component="h1">
          My notes today
        </Typography>
        <TextField
          select
          label="Care profile"
          value={child?.id || ""}
          onChange={(event) => {
            setCurrentChildId(event.target.value);
            setChosenDay("");
            setEditing(null);
            setSigning(null);
            setWithdrawing(null);
          }}
        >
          {childrenWithAccess.map((child) => (
            <MenuItem key={child.id} value={child.id}>
              {child.name || child.firstName || "Care profile"}
            </MenuItem>
          ))}
        </TextField>
        {!enabled ? (
          <Alert severity="info">
            My notes today is available to Staff caregivers. Ask the Care Owner
            to set your caregiver type and staff title.
          </Alert>
        ) : null}
        {data.error ? (
          <Alert severity="error">
            {data.error}
            <Button onClick={data.refresh}>Retry loading</Button>
          </Alert>
        ) : null}
        {data.context ? (
          <>
            <Typography>
              {data.context.personName} · Written by {data.context.signerName} ·{" "}
              {data.context.timeZone}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label={
                  view === "documented" ? "Documentation date" : "Care date"
                }
                type="date"
                value={day}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setChosenDay(event.target.value)}
              />
              <TextField
                select
                label="Show"
                value={view}
                onChange={(event) => setView(event.target.value)}
              >
                <MenuItem value="care">Care on this day</MenuItem>
                <MenuItem value="documented">Documented on this day</MenuItem>
                <MenuItem value="undated">Care date not recorded</MenuItem>
                <MenuItem value="unsigned">All unsigned notes</MenuItem>
              </TextField>
            </Stack>
            <Button
              onClick={data.refresh}
              disabled={busy || data.loading}
              sx={{ alignSelf: "flex-start" }}
            >
              Refresh notes
            </Button>
            <Typography>
              {counts.total} notes ·{" "}
              {counts.signed} signed ·{" "}
              {counts.pending} unsigned ·{" "}
              {ready.length} ready to sign
            </Typography>
            {older ? (
              <Button onClick={() => setView("unsigned")}>
                {older} older notes still unsigned
              </Button>
            ) : null}
            {otherCareDates ? (
              <Button
                onClick={() => {
                  setChosenDay(today);
                  setView("documented");
                }}
              >
                {otherCareDates} notes documented today are under another or
                missing care date
              </Button>
            ) : null}
            {undated ? (
              <Button onClick={() => setView("undated")}>
                {undated} notes need a care date
              </Button>
            ) : null}
            {message ? <Alert severity="info">{message}</Alert> : null}
            {data.loading ? (
              <Typography>Loading your original notes…</Typography>
            ) : null}
            {!data.loading && !visible.length ? (
              <Typography>No notes in this view.</Typography>
            ) : null}
            {visible.map((note) => (
              <StaffNoteCard
                key={note.captureId}
                note={note}
                context={data.context}
                busy={busy || data.loading || Boolean(data.error)}
                onEdit={() => setEditing({ note, operation: "edit" })}
                onAddendum={() =>
                  setEditing({
                    note,
                    operation: "addendum",
                    requestId: createClientRequestId("correction"),
                  })
                }
                onSign={() => setSigning([note])}
                onConfirmTranscript={() =>
                  action(() => change(note, "confirm_transcript"))
                }
                onRetry={() => action(() => change(note, "retry"))}
                onWithdraw={() => setWithdrawing(note)}
              />
            ))}
            <Button
              variant="contained"
              disabled={
                busy || data.loading || Boolean(data.error) || !ready.length
              }
              onClick={() => setSigning(visible.filter(note => !note.signature).map((note) => ({ ...note })))}
            >
              Review and sign {ready.length} ready notes
            </Button>
            {withdrawing && withdrawing.authorId === user?.uid && withdrawing.childId === child?.id ? (
              <StaffNoteWithdrawalDialog
                key={withdrawing.captureId}
                note={withdrawing}
                onClose={() => setWithdrawing(null)}
                onWithdraw={async note => {
                  await change(note, "withdraw");
                  setMessage("Unsigned original deleted. Its version history is retained.");
                }}
              />
            ) : null}
            {editing ? (
              <StaffNoteEditor
                key={`${editing.note.captureId}-${editing.operation}`}
                {...editing}
                onClose={() => setEditing(null)}
                onSave={(changes) =>
                  change(editing.note, editing.operation, {
                    ...changes,
                    ...(editing.requestId
                      ? { requestId: editing.requestId }
                      : {}),
                  })
                }
              />
            ) : null}
            {signing ? (
              <StaffSigningDialog
                notes={signing}
                context={data.context}
                attestation={data.attestation}
                onSign={sign}
                onClose={() => setSigning(null)}
              />
            ) : null}
          </>
        ) : null}
      </Stack>
    </Container>
  );
}
